// / (home) — org picker + single-org redirect (PRD §9.4 +
// docs/ux_updates.md "Org / project landing", B1f PR3).
//
// The user already has `me.organizations` in the auth store from
// `auth.ts → fetchMe()`, so this route reads from the store rather
// than firing a separate query. Three branches:
//
//   1. Zero orgs       → "no orgs yet" empty state. Sign-up flow
//                         provisions an org for solo users today, so
//                         the only way to land here is an admin
//                         removing the only membership; we still
//                         render something useful.
//   2. Exactly one org → redirect to /orgs/{slug}. Lands the user on
//                         the only place they can do work.
//   3. Two or more     → render an org picker so the user chooses.
//
// The redirect runs in `beforeLoad` (not in the component) so the
// browser never paints a flash of the picker before the redirect
// fires.

import { createFileRoute, Link, redirect } from '@tanstack/react-router';

import { EmptyState } from '../components/EmptyState';
import { useAuthStore } from '../stores/authStore';

export const Route = createFileRoute('/')({
  beforeLoad: () => {
    const orgs = useAuthStore.getState().user?.organizations ?? [];
    if (orgs.length === 1) {
      const only = orgs[0];
      if (only) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: '/orgs/$orgSlug',
          params: { orgSlug: only.organization.slug },
        });
      }
    }
  },
  component: HomeRoute,
});

function HomeRoute(): React.JSX.Element {
  const orgs = useAuthStore((state) => state.user?.organizations ?? []);

  return (
    <div className="mx-auto max-w-2xl py-7">
      <header className="mb-7">
        <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
          Home
        </p>
        <h1 className="text-text text-28 font-semibold">Choose an organization</h1>
        <p className="text-text-muted mt-3 text-13">
          You belong to {orgs.length} organizations. Pick one to continue.
        </p>
      </header>

      {orgs.length === 0 ? (
        <EmptyState
          title="No organizations yet"
          description="You aren’t a member of any organization. Ask an admin to invite you, or contact support."
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {orgs.map((membership) => (
            <li key={membership.organization.id}>
              <Link
                to="/orgs/$orgSlug"
                params={{ orgSlug: membership.organization.slug }}
                className="border-border bg-bg-elevated hover:border-border-strong rounded-2 block border p-4 transition-colors"
              >
                <p className="text-text text-15 font-semibold">
                  {membership.organization.name}
                </p>
                <p className="text-text-faint mt-1 font-mono text-11 uppercase tracking-wider">
                  {membership.organization.slug} · {membership.role}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
